import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';

export interface UserAnnouncements {
  lightModeAnnouncement?: {
    seen: boolean;
    seenAt?: Date;
    dismissed: boolean;
    dismissedAt?: Date;
  };
  colorThemeAnnouncement?: {
    seen: boolean;
    seenAt?: Date;
    dismissed: boolean;
    dismissedAt?: Date;
  };
}

class AnnouncementService {
  private readonly COLLECTION_NAME = 'userAnnouncements';

  // Récupérer les annonces d'un utilisateur
  async getUserAnnouncements(userId: string): Promise<UserAnnouncements> {
    try {
      const docRef = doc(db, this.COLLECTION_NAME, userId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        // Convertir les timestamps en dates
        if (data.lightModeAnnouncement) {
          if (data.lightModeAnnouncement.seenAt) {
            data.lightModeAnnouncement.seenAt = data.lightModeAnnouncement.seenAt.toDate();
          }
          if (data.lightModeAnnouncement.dismissedAt) {
            data.lightModeAnnouncement.dismissedAt = data.lightModeAnnouncement.dismissedAt.toDate();
          }
        }
        if (data.colorThemeAnnouncement) {
          if (data.colorThemeAnnouncement.seenAt) {
            data.colorThemeAnnouncement.seenAt = data.colorThemeAnnouncement.seenAt.toDate();
          }
          if (data.colorThemeAnnouncement.dismissedAt) {
            data.colorThemeAnnouncement.dismissedAt = data.colorThemeAnnouncement.dismissedAt.toDate();
          }
        }
        return data as UserAnnouncements;
      }
      
      return {};
    } catch (error) {
      // console.error supprimé
      return {};
    }
  }

  // Marquer l'annonce du mode light comme vue (plus besoin de sauvegarder, le ThemeContext s'en charge)
  async markLightModeAnnouncementAsSeen(_userId: string): Promise<void> {
    // Plus besoin de sauvegarder - le ThemeContext va sauvegarder le thème choisi dans userPreferences
    return Promise.resolve();
  }

  // Marquer l'annonce du mode light comme ignorée (plus besoin de sauvegarder)
  async dismissLightModeAnnouncement(_userId: string): Promise<void> {
    // Plus besoin de sauvegarder - on considère que l'utilisateur a fait son choix
    return Promise.resolve();
  }

  // Vérifier si l'utilisateur doit voir l'annonce du mode light
  async shouldShowLightModeAnnouncement(userId: string): Promise<boolean> {
    try {
      // Vérification instantanée dans le localStorage user-specific
      const localTheme = localStorage.getItem(`focuspips-theme_${userId}`);
      if (localTheme === 'dark' || localTheme === 'light') {
        return false;
      }

      // Vérifier directement dans userPreferences si le thème est défini
      const userPrefsRef = doc(db, 'userPreferences', userId);
      const userPrefsSnap = await getDoc(userPrefsRef);
      
      if (userPrefsSnap.exists()) {
        const data = userPrefsSnap.data();
        // Si le champ theme existe, l'utilisateur a déjà fait un choix
        if (data.theme) {
          return false;
        }
      }
      
      // Si pas de préférences ou pas de thème défini, afficher le popup
      return true;
    } catch (error) {
      // console.error supprimé
      return false;
    }
  }

  // Marquer l'annonce du thème de couleurs comme vue
  async markColorThemeAnnouncementAsSeen(userId: string): Promise<void> {
    try {
      const docRef = doc(db, this.COLLECTION_NAME, userId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        await updateDoc(docRef, {
          'colorThemeAnnouncement.seen': true,
          'colorThemeAnnouncement.seenAt': new Date()
        });
      } else {
        await setDoc(docRef, {
          colorThemeAnnouncement: {
            seen: true,
            seenAt: new Date(),
            dismissed: false
          }
        });
      }
    } catch (error) {
      // console.error supprimé
    }
  }

  // Marquer l'annonce du thème de couleurs comme ignorée
  async dismissColorThemeAnnouncement(userId: string): Promise<void> {
    try {
      const docRef = doc(db, this.COLLECTION_NAME, userId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        await updateDoc(docRef, {
          'colorThemeAnnouncement.dismissed': true,
          'colorThemeAnnouncement.dismissedAt': new Date()
        });
      } else {
        await setDoc(docRef, {
          colorThemeAnnouncement: {
            seen: false,
            dismissed: true,
            dismissedAt: new Date()
          }
        });
      }
    } catch (error) {
      // console.error supprimé
    }
  }

  // Vérifier si l'utilisateur doit voir l'annonce du thème de couleurs
  async shouldShowColorThemeAnnouncement(userId: string): Promise<boolean> {
    try {
      // Vérifier si l'annonce a déjà été vue ou ignorée
      const announcements = await this.getUserAnnouncements(userId);
      
      if (announcements.colorThemeAnnouncement?.seen || announcements.colorThemeAnnouncement?.dismissed) {
        return false;
      }

      // Vérifier si l'utilisateur a déjà un thème de couleur défini dans Firebase
      const userPrefsRef = doc(db, 'userPreferences', userId);
      const userPrefsSnap = await getDoc(userPrefsRef);
      
      if (userPrefsSnap.exists()) {
        const data = userPrefsSnap.data();
        // Si un thème de couleur est défini (même 'default'), considérer que l'utilisateur a fait un choix
        if (data.iaAnalysis?.colorTheme) {
          return false;
        }
      }

      // Vérifier aussi le localStorage comme fallback
      const colorTheme = localStorage.getItem(`colorTheme_${userId}`);
      if (colorTheme) {
        return false;
      }
      
      // Afficher l'annonce si c'est la première fois
      return true;
    } catch (error) {
      // console.error supprimé
      return false;
    }
  }
}

export const announcementService = new AnnouncementService(); 
